import React, { Suspense, lazy, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import CountUp from "react-countup";
import {
  Activity,
  CalendarCheck,
  Sparkles,
  TrendingUp,
  Users,
} from "lucide-react";

import { getDashboardSummary } from "services/dashboard.service";
import { cn } from "lib/utils";
import { Skeleton } from "components/ui/skeleton";
import { Badge } from "components/ui/badge";

import LeadEngagementCard from "./LeadEngagementCard";
import OutreachFunnelCard from "./OutreachFunnelCard";
import AppointmentsOverview from "./AppointmentsOverview";
import "./Dashboard.scss";

const DashboardHeroScene = lazy(
  () => import("components/three/DashboardHeroScene")
);

interface LeadEngagementData {
  channelBreakdown: {
    channels: Array<{ name: string; count: number; percentage: number }>;
  };
  totalContacted: number;
  engaged: number;
  engagementRate: number;
  topCampaigns: Array<{
    name: string;
    subject: string;
    engagementRate: number;
    contacted: number;
    engaged: number;
  }>;
}

interface OutreachConversionData {
  funnelStageData: Array<{
    title: string;
    value: number;
    conversionRate: number;
  }>;
  campaignPerformanceData: Array<{
    name: string;
    contacted: number;
    appointments: number;
    conversion: number;
  }>;
}

interface AppointmentOverviewData {
  metrics: {
    totalAppointments: number;
    totalAppointmentsInDay: number;
    totalAppointmentsInWeek: number;
    completedAppointments: number;
    cancelledAppointments: number;
    tentativeAppointments: number;
    completionRate: number;
    cancellationRate: number;
    tentativeRate: number;
    totalAppointmentsByAI: number;
    totalAppointmentsByStaff: number;
  };
  chartData: {
    series: Array<{
      name: string;
      data: number[];
      total: number;
      totalConfirmed: number;
      totalRejected: number;
    }>;
    categories: string[];
  };
}

const Hero: React.FC<{
  totalContacted: number;
  engaged: number;
  engagementRate: number;
  appointmentsToday: number;
}> = ({ totalContacted, engaged, engagementRate, appointmentsToday }) => {
  const userName = (
    typeof window !== "undefined" ? localStorage.getItem("name") : ""
  ) || "there";

  const quickStats = [
    {
      icon: Users,
      label: "Total contacted",
      value: totalContacted,
      accent: "text-primary",
      bg: "bg-primary/10",
    },
    {
      icon: TrendingUp,
      label: "Engaged",
      value: engaged,
      accent: "text-success",
      bg: "bg-success/10",
    },
    {
      icon: Activity,
      label: "Engagement rate",
      value: engagementRate,
      suffix: "%",
      accent: "text-accent",
      bg: "bg-accent/10",
    },
    {
      icon: CalendarCheck,
      label: "Appointments today",
      value: appointmentsToday,
      accent: "text-warning",
      bg: "bg-warning/10",
    },
  ];

  return (
    <section className="relative overflow-hidden rounded-2xl border border-border/60 bg-card shadow-soft">
      {/* WebGL backdrop */}
      <div className="absolute inset-0 -z-0">
        <Suspense
          fallback={
            <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-accent/15 to-primary/20" />
          }
        >
          <DashboardHeroScene className="!absolute inset-0 h-full w-full" />
        </Suspense>
        <div
          aria-hidden
          className="absolute inset-0 bg-gradient-to-r from-card via-card/60 to-transparent"
        />
        <div
          aria-hidden
          className="absolute inset-0 bg-gradient-to-t from-card/80 via-transparent to-transparent"
        />
      </div>

      <div className="relative z-10 grid gap-6 px-6 py-8 sm:px-10 sm:py-10 lg:grid-cols-[1fr_auto] lg:items-end">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: "easeOut" }}
          className="max-w-xl"
        >
          <Badge variant="soft" className="mb-4 gap-1.5">
            <Sparkles className="h-3 w-3" />
            Marketing intelligence
          </Badge>
          <h1 className="text-2xl font-semibold tracking-tight text-foreground sm:text-3xl lg:text-4xl">
            Welcome back,{" "}
            <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
              {userName}
            </span>
          </h1>
          <p className="mt-2 max-w-md text-sm text-muted-foreground sm:text-base">
            Here's how your campaigns, conversations, and appointments are
            performing today.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, delay: 0.1, ease: "easeOut" }}
          className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:max-w-xl"
        >
          {quickStats.map(({ icon: Icon, label, value, suffix, accent, bg }) => (
            <div
              key={label}
              className="rounded-xl border border-border/50 bg-card/60 px-4 py-3 backdrop-blur-md"
            >
              <div className="mb-2 flex items-center gap-2">
                <span
                  className={cn(
                    "grid h-7 w-7 place-items-center rounded-lg",
                    bg,
                    accent
                  )}
                >
                  <Icon className="h-3.5 w-3.5" />
                </span>
                <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                  {label}
                </span>
              </div>
              <p className="text-xl font-semibold text-foreground sm:text-2xl">
                <CountUp end={value || 0} duration={1.4} separator="," />
                {suffix || ""}
              </p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

const DashboardSkeleton: React.FC = () => (
  <div className="space-y-6">
    <Skeleton className="h-56 w-full rounded-2xl" />
    <div className="grid gap-4 lg:grid-cols-3">
      {Array.from({ length: 3 }).map((_, i) => (
        <Skeleton key={i} className="h-28 rounded-xl" />
      ))}
    </div>
    <Skeleton className="h-96 w-full rounded-xl" />
    <Skeleton className="h-96 w-full rounded-xl" />
  </div>
);

const Dashboard: React.FC = () => {
  const [leadEngagementData, setLeadEngagementData] =
    useState<LeadEngagementData | null>(null);
  const [outreachConversionData, setOutreachConversionData] =
    useState<OutreachConversionData | null>(null);
  const [appointmentOverviewData, setAppointmentOverviewData] =
    useState<AppointmentOverviewData | null>(null);
  const [loading, setLoading] = useState(true);

  const tableHeaders = [
    { key: "name", label: "Campaigns" },
    { key: "engagement", label: "Engagement" },
    { key: "contacted", label: "Contacted" },
    { key: "engaged", label: "Engaged" },
  ];

  const getChartOptions = (labels: string[]) => ({
    chart: { type: "donut" as const, height: window.innerWidth < 640 ? 250 : 300 },
    labels,
    colors: ["#6366f1", "#22d3ee"],
    dataLabels: {
      enabled: true,
      formatter: (val: any) => Math.round(val) + "%",
      style: {
        fontSize: window.innerWidth < 640 ? "12px" : "14px",
        fontWeight: "bold" as const,
        colors: ["#fff"],
      },
    },
    plotOptions: {
      pie: { donut: { size: "70%", labels: { show: false } } },
    },
    legend: { show: false },
    stroke: { width: 0 },
    tooltip: { enabled: true },
  });

  const chartData = useMemo(() => {
    if (!leadEngagementData?.channelBreakdown?.channels) {
      return {
        labels: ["Email", "SMS"],
        series: [0, 0] as number[],
        legends: [
          { color: "bg-primary", label: "text-primary", value: "Email: 0%", count: 0 },
          { color: "bg-success", label: "text-success", value: "SMS: 0%", count: 0 },
        ],
      };
    }
    const channels = leadEngagementData.channelBreakdown.channels;
    return {
      labels: channels.map((c) => c.name),
      series: channels.map((c) => c.percentage),
      legends: channels.map((c, i) => ({
        color: i === 0 ? "bg-primary" : "bg-success",
        label: i === 0 ? "text-primary" : "text-success",
        value: `${c.name}: ${c.count} (${c.percentage}%)`,
        count: c.count,
      })),
    };
  }, [leadEngagementData]);

  const statCards = useMemo(() => {
    const d = leadEngagementData;
    return [
      {
        title: "Total Contacted",
        value: (d?.totalContacted ?? 0).toLocaleString(),
        accent: "text-primary",
        bg: "bg-primary/5",
        border: "border-primary/20",
        icon: Users,
      },
      {
        title: "Engaged",
        value: (d?.engaged ?? 0).toLocaleString(),
        accent: "text-success",
        bg: "bg-success/5",
        border: "border-success/20",
        icon: TrendingUp,
      },
      {
        title: "Engagement Rate",
        value: `${d?.engagementRate ?? 0}%`,
        accent: "text-accent",
        bg: "bg-accent/5",
        border: "border-accent/20",
        icon: Activity,
      },
    ];
  }, [leadEngagementData]);

  const campaignData = useMemo(() => {
    if (!leadEngagementData?.topCampaigns) {
      return [
        {
          name: "No campaigns",
          subject: "No subject",
          engagement: "0%",
          contacted: "0",
          engaged: "0",
        },
      ];
    }
    return leadEngagementData.topCampaigns.map((c) => ({
      name: c.name,
      subject: c.subject,
      engagement: `${c.engagementRate}%`,
      contacted: c.contacted.toLocaleString(),
      engaged: c.engaged.toLocaleString(),
    }));
  }, [leadEngagementData]);

  const outreachFunnelData = useMemo(() => {
    if (!outreachConversionData?.funnelStageData) {
      return [
        { title: "Leads Messaged", value: 0, label: "Leads Messaged", conversionRate: 0 },
        { title: "Engaged", value: 0, label: "Engaged", conversionRate: 0 },
        { title: "Appointments", value: 0, label: "Appointments", conversionRate: 0 },
      ];
    }
    return outreachConversionData.funnelStageData.map((stage) => ({
      title: stage.title,
      value: stage.value,
      label: stage.title,
      conversionRate: stage.conversionRate,
    }));
  }, [outreachConversionData]);

  const outreachCampaignData = useMemo(() => {
    if (!outreachConversionData?.campaignPerformanceData) return [];
    return outreachConversionData.campaignPerformanceData.map((c) => ({
      name: c.name,
      contacted: c.contacted,
      appointments: c.appointments,
      conversion: c.conversion,
    }));
  }, [outreachConversionData]);

  const appointmentsStatCards = useMemo(() => {
    const m = appointmentOverviewData?.metrics;
    return [
      {
        title: "Today",
        value: m?.totalAppointmentsInDay ?? 0,
        accent: "text-primary",
        bg: "bg-primary/5",
        border: "border-primary/20",
        icon: "📅",
      },
      {
        title: "This Week",
        value: m?.totalAppointmentsInWeek ?? 0,
        accent: "text-primary",
        bg: "bg-primary/5",
        border: "border-primary/20",
        icon: "📅",
      },
      {
        title: "Confirmed",
        value: m?.completedAppointments ?? 0,
        accent: "text-success",
        bg: "bg-success/5",
        border: "border-success/20",
        icon: "✅",
        isPercentage: true,
      },
      {
        title: "Cancelled",
        value: m?.cancelledAppointments ?? 0,
        accent: "text-destructive",
        bg: "bg-destructive/5",
        border: "border-destructive/20",
        icon: "❌",
      },
      {
        title: "Tentative",
        value: m?.tentativeAppointments ?? 0,
        accent: "text-warning",
        bg: "bg-warning/5",
        border: "border-warning/20",
        icon: "⚠️",
      },
    ];
  }, [appointmentOverviewData]);

  const appointmentsChartData = useMemo(() => {
    if (!appointmentOverviewData) {
      return {
        series: [0, 0],
        legends: [
          { color: "bg-primary", label: "text-primary", value: "AI Scheduled: 0%" },
          { color: "bg-success", label: "text-success", value: "Staff Scheduled: 0%" },
        ],
      };
    }
    const { metrics } = appointmentOverviewData;
    const total =
      metrics.totalAppointmentsByAI + metrics.totalAppointmentsByStaff;
    const aiPct = total > 0 ? Math.round((metrics.totalAppointmentsByAI / total) * 100) : 0;
    const staffPct = total > 0 ? Math.round((metrics.totalAppointmentsByStaff / total) * 100) : 0;
    return {
      series: [aiPct, staffPct],
      legends: [
        { color: "bg-primary", label: "text-primary", value: `AI Scheduled: ${aiPct}%` },
        { color: "bg-success", label: "text-success", value: `Staff Scheduled: ${staffPct}%` },
      ],
    };
  }, [appointmentOverviewData]);

  const appointmentsBarChartData = useMemo(() => {
    if (!appointmentOverviewData?.chartData.series.length) {
      return {
        series: [{ name: "Appointments", data: [0, 0, 0, 0, 0, 0, 0] }],
        categories: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
      };
    }
    return {
      series: appointmentOverviewData.chartData.series,
      categories: appointmentOverviewData.chartData.categories,
    };
  }, [appointmentOverviewData]);

  const appointmentsSummaryStats = useMemo(() => {
    if (!appointmentOverviewData) {
      return [
        { color: "bg-primary", label: "Total Appointments", value: 0 },
        { color: "bg-success", label: "Confirmed", value: 0 },
        { color: "bg-destructive", label: "Cancelled", value: 0 },
      ];
    }
    const { chartData } = appointmentOverviewData;
    return [
      { color: "bg-primary", label: "Total Appointments", value: chartData.series[0].total },
      { color: "bg-success", label: "Confirmed", value: chartData.series[0].totalConfirmed },
      { color: "bg-destructive", label: "Cancelled", value: chartData.series[0].totalRejected },
    ];
  }, [appointmentOverviewData]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        getDashboardSummary().then((dashboardSummaryData: any) => {
          if (!dashboardSummaryData) return;
          setLeadEngagementData(
            dashboardSummaryData?.leadEngagementSummary ||
              dashboardSummaryData?.data
          );
          setOutreachConversionData(
            dashboardSummaryData?.outreachConversionSummary || null
          );
          setAppointmentOverviewData(
            dashboardSummaryData?.appointmentOverview || null
          );
          setLoading(false);
        });
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
        setLoading(false);
      }
    };
    fetchDashboardData();
  }, []);

  const chartOptions = getChartOptions(chartData.labels);

  const appointmentsChartOptions = {
    chart: { type: "donut" as const, height: window.innerWidth < 640 ? 200 : 280 },
    labels: ["AI Scheduled", "Staff Scheduled"],
    colors: ["#6366f1", "#22c55e"],
    dataLabels: {
      enabled: true,
      formatter: (val: any) => Math.round(val) + "%",
      style: {
        fontSize: window.innerWidth < 640 ? "12px" : "14px",
        fontWeight: "bold" as const,
        colors: ["#fff"],
      },
    },
    plotOptions: {
      pie: { donut: { size: "70%", labels: { show: false } } },
    },
    legend: { show: false },
    stroke: { width: 0 },
    tooltip: { enabled: true },
  };

  const appointmentsBarChartOptions = {
    chart: {
      type: "bar" as const,
      height: window.innerWidth < 640 ? 200 : 280,
      toolbar: { show: false },
      foreColor: "hsl(var(--muted-foreground))",
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: window.innerWidth < 640 ? "80%" : "55%",
        borderRadius: 6,
      },
    },
    dataLabels: { enabled: false },
    stroke: { show: true, width: 2, colors: ["transparent"] },
    xaxis: {
      categories: appointmentsBarChartData.categories,
      axisBorder: { show: false },
      axisTicks: { show: false },
      labels: {
        style: { fontSize: window.innerWidth < 640 ? "10px" : "12px" },
      },
    },
    yaxis: {
      labels: { style: { fontSize: window.innerWidth < 640 ? "10px" : "12px" } },
    },
    fill: { opacity: 1, colors: ["#6366f1"] },
    grid: { show: true, borderColor: "hsl(var(--border))", strokeDashArray: 3 },
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <DashboardSkeleton />
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="space-y-6"
    >
      <Hero
        totalContacted={leadEngagementData?.totalContacted ?? 0}
        engaged={leadEngagementData?.engaged ?? 0}
        engagementRate={leadEngagementData?.engagementRate ?? 0}
        appointmentsToday={
          appointmentOverviewData?.metrics?.totalAppointmentsInDay ?? 0
        }
      />

      <LeadEngagementCard
        chartOptions={chartOptions}
        chartSeries={chartData.series}
        statCards={statCards}
        chartLegends={chartData.legends}
        tableHeaders={tableHeaders}
        tableData={campaignData}
      />

      <OutreachFunnelCard
        funnelData={outreachFunnelData}
        campaignData={outreachCampaignData}
      />

      <AppointmentsOverview
        title="Appointments Overview"
        chartOptions={appointmentsChartOptions}
        chartSeries={appointmentsChartData.series}
        statCards={appointmentsStatCards}
        chartLegends={appointmentsChartData.legends}
        barChartOptions={appointmentsBarChartOptions}
        barChartSeries={appointmentsBarChartData.series}
        summaryStats={appointmentsSummaryStats}
      />
    </motion.div>
  );
};

export default Dashboard;
